/**
 * Authentication port.
 *
 * The application only ever talks to this interface, exactly like the backend talks to its
 * `AuthProvider` port. There are two implementations:
 *
 *   supabase  the real one (ADR-002): password sign-in against an account whose address the
 *             provider has confirmed. That is the whole of it.
 *   local     a harness implementation used only by the offline end-to-end suite, which runs the
 *             real backend with its own `AUTH_PROVIDER=fake` port. It is refused in a production
 *             build (see `createAuthAdapter`), mirroring the backend's
 *             "fake auth provider is forbidden in production" rule.
 *
 * WHAT IS NOT HERE, AND WHY (PO 7.10)
 * -----------------------------------
 * There is no `verifyTotp`, no `enrollTotp` and no `confirmTotpEnrollment`. The second factor was
 * removed from the admin flow, and leaving the methods behind would leave a route back to the
 * screens that use them — a stale render, a bookmark, a future edit. A capability that cannot be
 * called is a capability that cannot be reinstated by accident.
 *
 * What that deliberately does NOT do is touch anybody's account. Nothing here unenrols a factor,
 * and nothing asks the provider for one: a TOTP factor somebody enrolled under the old rule is
 * still enrolled, still theirs, and still there if the decision is ever reversed. The application
 * simply stops asking.
 *
 * No implementation stores a password. The password is handed to the provider and dropped.
 */
import type { RecoveryGrant } from './recovery-link';

export type { RecoveryGrant } from './recovery-link';

export type AuthStatus =
  /** No session at all. */
  | 'signed_out'
  /**
   * A session whose access token can be sent. Whether the person may DO anything is a separate
   * question, answered by the backend from our own database — this only says a token exists.
   */
  | 'ready';

export interface AuthState {
  status: AuthStatus;
  email: string | null;
  /**
   * Assurance level as reported by the provider, when it reports one.
   *
   * Carried because it is true and occasionally worth seeing; it is not a gate. `aal1` is an
   * ordinary, fully usable admin session since PO 7.10, and nothing in the application branches on
   * this field.
   */
  aal: 'aal1' | 'aal2' | null;
  /** True when the provider has a session whose access token can be sent. */
  hasToken: boolean;
}

/**
 * What a consumed recovery link established.
 *
 * Deliberately carries no token, no identifier and no assurance level: the recovery screen needs
 * to name the account it is about to change and nothing else, and a value that is not returned is
 * a value that cannot reach a state, a snapshot or a log.
 */
export interface RecoverySession {
  email: string | null;
}

export interface AuthAdapter {
  readonly name: string;
  /** Reads any persisted session on start-up. */
  restore(): Promise<AuthState>;
  signInWithPassword(email: string, password: string): Promise<AuthState>;
  signOut(): Promise<void>;
  getAccessToken(): Promise<string | null>;
  /** Subscribes to provider-side changes (refresh, sign-out in another tab). */
  onChange(listener: (state: AuthState) => void): () => void;

  /*
   * Password recovery: three methods, and the reason each one exists.
   *
   * A recovery link is NOT a sign-in. It establishes a session of its own, separate from the one
   * `signInWithPassword` builds, which may do exactly one thing — set a new password — and is then
   * retired. Keeping it on its own methods is what keeps the two apart: nothing here feeds
   * `AuthState`, so a consumed link can never turn into a console session, and an existing console
   * session can never be mistaken for a recovery.
   *
   * Asking for the mail is the fourth, and it is deliberately the same kind of thing: it sends a
   * request and returns nothing. No `AuthState`, no session, no sign-in — a person who asks for a
   * recovery mail is still signed out, and the only way forward is the link that arrives.
   */

  /** Whether this implementation can complete a recovery link at all. */
  readonly supportsRecovery: boolean;
  /**
   * Asks the provider to send a recovery mail to this address, and answers nothing about it.
   *
   * `void` on purpose: whether the address belongs to an account is not something a caller may
   * learn, so there is no value to return and no shape in which existence could be reported. The
   * screen says the same sentence either way. It throws only for what is about the REQUEST rather
   * than the account — a provider that refused to take it — and those failures carry no provider
   * text (see `AUTH_MESSAGES`).
   */
  requestPasswordRecovery(email: string): Promise<void>;
  /**
   * Consumes the grant the address carried and establishes the recovery session. Throws an
   * `AuthFailure` on anything the provider will not accept — an expired link, a spent one, a
   * session it cannot verify — so the caller never has to decide whether a half-answer is good
   * enough.
   */
  beginPasswordRecovery(grant: RecoveryGrant): Promise<RecoverySession>;
  /** Writes the new password onto the recovery session. Throws if there is no such session. */
  completePasswordRecovery(newPassword: string): Promise<void>;
  /** Retires the recovery session without touching the ordinary one. Safe to call twice. */
  endPasswordRecovery(): Promise<void>;
}

export const SIGNED_OUT: AuthState = { status: 'signed_out', email: null, aal: null, hasToken: false };
